import React, { useState, useRef, useCallback } from 'react';
import axios from '../utils/authInterceptor';
import TappyLabsBranding from './TappyLabsBranding';
import ToastNotification from './ToastNotification';

const EssayUpload = ({ onClose, onUploadSuccess }) => {
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, success, error
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadConfig, setUploadConfig] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const fileInputRef = useRef(null);

  // Load upload configuration on component mount
  React.useEffect(() => {
    const loadConfig = async () => {
      try {
        console.log('Loading upload config...');
        const response = await axios.get('/api/essays/upload-config');
        console.log('Upload config response:', response);
        if (response.data.success) {
          setUploadConfig(response.data.config);
          console.log('Upload config loaded:', response.data.config);
        } else {
          console.error('Upload config failed:', response.data);
          showToast(`Config error: ${response.data.error}`, 'error');
        }
      } catch (error) {
        console.error('Failed to load upload config:', error);
        console.error('Error details:', error.response?.data);
        showToast('Failed to load upload configuration', 'error');
      }
    };

    loadConfig();
  }, []);

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: '', type: '' });
  };

  // File validation
  const validateFile = (file) => {
    if (!uploadConfig) return { valid: false, errors: ['Upload configuration not loaded'] };
    
    const errors = [];
    
    // Check file type
    if (!uploadConfig.allowed_extensions.some(ext => 
      file.name.toLowerCase().endsWith(`.${ext.toLowerCase()}`)
    )) {
      errors.push(`Only ${uploadConfig.allowed_extensions.join(', ')} files are allowed`);
    }
    
    // Check file size
    if (file.size > uploadConfig.max_file_size_bytes) {
      errors.push(`File size must be less than ${uploadConfig.max_file_size_mb}MB`);
    }
    
    return { valid: errors.length === 0, errors };
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    const validation = validateFile(file);
    
    if (!validation.valid) {
      setErrorMessage(validation.errors.join('. '));
      setSelectedFile(null);
      return;
    }
    
    setErrorMessage('');
    setSelectedFile(file);
  };

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [uploadConfig]);

  // Handle file input change
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Upload file
  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploadState('uploading');
    setUploadProgress(0);
    setErrorMessage('');
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const response = await axios.post('/api/essays/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        }
      });
      
      if (response.data.success) {
        setUploadState('success');
        showToast('Essay uploaded successfully! Processing will begin shortly.', 'success');
        if (onUploadSuccess) {
          onUploadSuccess(response.data.file_info);
        }
        
        // Close modal after a delay
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      setUploadState('error');
      const errorMsg = error.response?.data?.error || error.message || 'Upload failed';
      setErrorMessage(errorMsg);
      showToast(`Upload failed: ${errorMsg}`, 'error');
      console.error('Upload error:', error);
    }
  };

  // Reset upload state
  const resetUpload = () => {
    setUploadState('idle');
    setUploadProgress(0);
    setSelectedFile(null);
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: TappyLabsBranding.colors.transparency.overlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(10px)'
      }}>
        {/* Modal Container */}
        <div style={{
          ...TappyLabsBranding.components.glassCard,
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          padding: '32px',
          position: 'relative',
          overflow: 'auto'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: `1px solid ${TappyLabsBranding.colors.transparency.medium}`
          }}>
            <h2 style={{
              margin: 0,
              fontFamily: TappyLabsBranding.typography.fonts.headers,
              fontSize: TappyLabsBranding.typography.scale['2xl'],
              fontWeight: TappyLabsBranding.typography.weights.semibold,
              color: TappyLabsBranding.colors.primary.deepOcean,
              background: TappyLabsBranding.colors.gradients.aquariumDepth,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              📄 Upload Essay for Analysis
            </h2>
            
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: TappyLabsBranding.colors.semantic.neutral,
                padding: '8px',
                borderRadius: '8px',
                transition: `all ${TappyLabsBranding.animations.durations.normal} ${TappyLabsBranding.animations.easing.ease}`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = TappyLabsBranding.colors.transparency.subtle;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
              }}
            >
              ×
            </button>
          </div>

          {/* Upload States */}
          {uploadState === 'idle' && (
            <>
              {/* Upload Zone */}
              <div
                style={{
                  ...TappyLabsBranding.components.aquariumContainer,
                  background: dragActive 
                    ? TappyLabsBranding.colors.gradients.ecosystemFlow 
                    : TappyLabsBranding.colors.gradients.crystalClear,
                  border: dragActive 
                    ? `2px dashed ${TappyLabsBranding.colors.primary.learningAquariumBlue}`
                    : `2px dashed ${TappyLabsBranding.colors.transparency.medium}`,
                  padding: '48px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  marginBottom: '24px',
                  transition: `all ${TappyLabsBranding.animations.durations.normal} ${TappyLabsBranding.animations.easing.ease}`
                }}
                onDragEnter={handleDragIn}
                onDragLeave={handleDragOut}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px',
                  animation: dragActive ? `${TappyLabsBranding.animations.keyframes.float} 2s ease-in-out infinite` : 'none'
                }}>
                  {dragActive ? '🌊' : '📄'}
                </div>
                
                <p style={{
                  fontFamily: TappyLabsBranding.typography.fonts.body,
                  fontSize: TappyLabsBranding.typography.scale.lg,
                  fontWeight: TappyLabsBranding.typography.weights.medium,
                  color: TappyLabsBranding.colors.primary.deepOcean,
                  margin: '0 0 8px 0'
                }}>
                  {dragActive ? 'Drop your essay here!' : 'Drag & drop your essay here'}
                </p>
                
                <p style={{
                  fontFamily: TappyLabsBranding.typography.fonts.body,
                  fontSize: TappyLabsBranding.typography.scale.sm,
                  color: TappyLabsBranding.colors.semantic.neutral,
                  margin: '0 0 16px 0'
                }}>
                  or click to browse
                </p>
                
                {uploadConfig && (
                  <div style={{
                    ...TappyLabsBranding.components.tappologyIndicator,
                    display: 'inline-block',
                    fontSize: TappyLabsBranding.typography.scale.xs
                  }}>
                    Max {uploadConfig.max_file_size_mb}MB • {uploadConfig.allowed_extensions.join(', ').toUpperCase()} only
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept={uploadConfig ? uploadConfig.allowed_extensions.map(ext => `.${ext}`).join(',') : '.pdf'}
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />

              {/* Selected File Display */}
              {selectedFile && (
                <div style={{
                  ...TappyLabsBranding.components.glassCard,
                  padding: '16px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>📄</span>
                    <div>
                      <p style={{
                        margin: 0,
                        fontFamily: TappyLabsBranding.typography.fonts.body,
                        fontSize: TappyLabsBranding.typography.scale.base,
                        fontWeight: TappyLabsBranding.typography.weights.medium,
                        color: TappyLabsBranding.colors.primary.deepOcean
                      }}>
                        {selectedFile.name}
                      </p>
                      <p style={{
                        margin: 0,
                        fontFamily: TappyLabsBranding.typography.fonts.body,
                        fontSize: TappyLabsBranding.typography.scale.sm,
                        color: TappyLabsBranding.colors.semantic.neutral
                      }}>
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                  </div>
                  
                  <button
                    onClick={resetUpload}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '18px',
                      cursor: 'pointer',
                      color: TappyLabsBranding.colors.semantic.error,
                      padding: '4px'
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Error Message */}
              {errorMessage && (
                <div style={{
                  background: `rgba(239, 68, 68, 0.1)`,
                  border: `1px solid rgba(239, 68, 68, 0.3)`,
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '24px',
                  color: TappyLabsBranding.colors.semantic.error,
                  fontSize: TappyLabsBranding.typography.scale.sm
                }}>
                  {errorMessage}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={onClose}
                  style={{
                    ...TappyLabsBranding.components.buttons.secondary,
                    fontSize: TappyLabsBranding.typography.scale.base
                  }}
                >
                  Cancel
                </button>
                
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile}
                  style={{
                    ...TappyLabsBranding.components.buttons.primary,
                    fontSize: TappyLabsBranding.typography.scale.base,
                    opacity: !selectedFile ? 0.6 : 1,
                    cursor: !selectedFile ? 'not-allowed' : 'pointer'
                  }}
                >
                  Upload & Analyze
                </button>
              </div>
            </>
          )}

          {/* Uploading State */}
          {uploadState === 'uploading' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '64px',
                marginBottom: '24px',
                animation: `${TappyLabsBranding.animations.keyframes.float} 2s ease-in-out infinite`
              }}>
                🌊
              </div>
              
              <h3 style={{
                fontFamily: TappyLabsBranding.typography.fonts.headers,
                fontSize: TappyLabsBranding.typography.scale.xl,
                fontWeight: TappyLabsBranding.typography.weights.semibold,
                color: TappyLabsBranding.colors.primary.deepOcean,
                marginBottom: '16px'
              }}>
                Uploading Essay...
              </h3>
              
              {/* Progress Bar */}
              <div style={{
                background: TappyLabsBranding.colors.transparency.subtle,
                borderRadius: '12px',
                height: '8px',
                marginBottom: '16px',
                overflow: 'hidden'
              }}>
                <div style={{
                  background: TappyLabsBranding.colors.gradients.ecosystemFlow,
                  height: '100%',
                  width: `${uploadProgress}%`,
                  borderRadius: '12px',
                  transition: `width ${TappyLabsBranding.animations.durations.normal} ${TappyLabsBranding.animations.easing.ease}`
                }} />
              </div>
              
              <p style={{
                fontFamily: TappyLabsBranding.typography.fonts.body,
                fontSize: TappyLabsBranding.typography.scale.sm,
                color: TappyLabsBranding.colors.semantic.neutral,
                margin: 0
              }}>
                {uploadProgress}% complete
              </p>
            </div>
          )}

          {/* Success State */}
          {uploadState === 'success' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                ...TappyLabsBranding.components.confidenceFish,
                width: '64px',
                height: '64px',
                margin: '0 auto 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px'
              }}>
                ✨
              </div>
              
              <h3 style={{
                fontFamily: TappyLabsBranding.typography.fonts.headers,
                fontSize: TappyLabsBranding.typography.scale.xl,
                fontWeight: TappyLabsBranding.typography.weights.semibold,
                color: TappyLabsBranding.colors.secondary.ecosystemGreen,
                marginBottom: '16px'
              }}>
                Upload Successful!
              </h3>
              
              <p style={{
                fontFamily: TappyLabsBranding.typography.fonts.body,
                fontSize: TappyLabsBranding.typography.scale.base,
                color: TappyLabsBranding.colors.semantic.neutral,
                margin: 0
              }}>
                Your essay is now being processed. You'll receive results soon!
              </p>
            </div>
          )}

          {/* Error State */}
          {uploadState === 'error' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '64px',
                marginBottom: '24px'
              }}>
                ⚠️
              </div>
              
              <h3 style={{
                fontFamily: TappyLabsBranding.typography.fonts.headers,
                fontSize: TappyLabsBranding.typography.scale.xl,
                fontWeight: TappyLabsBranding.typography.weights.semibold,
                color: TappyLabsBranding.colors.semantic.error,
                marginBottom: '16px'
              }}>
                Upload Failed
              </h3>
              
              <p style={{
                fontFamily: TappyLabsBranding.typography.fonts.body,
                fontSize: TappyLabsBranding.typography.scale.base,
                color: TappyLabsBranding.colors.semantic.neutral,
                marginBottom: '24px'
              }}>
                {errorMessage}
              </p>
              
              <button
                onClick={resetUpload}
                style={{
                  ...TappyLabsBranding.components.buttons.primary,
                  fontSize: TappyLabsBranding.typography.scale.base
                }}
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <ToastNotification
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
        autoClose={4000}
      />
    </>
  );
};

export default EssayUpload;